import { useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';

/**
 * Swipe-to-delete wrapper for a single mobile card row.
 * - Swipe LEFT past 60px → reveals red Delete button on the right.
 * - Tap the button → calls onDelete (caller handles confirm + API).
 * - Swipe back right (or tap outside) → snaps closed.
 *
 * Designed for the mobile card lists (md:hidden); on desktop it just renders children.
 *
 * Usage:
 *   <SwipeRow onDelete={() => handleDelete(item)} testid={`swipe-${item.id}`}>
 *     <div className="card p-4">...item content...</div>
 *   </SwipeRow>
 */
export default function SwipeRow({ children, onDelete, label = 'Delete', testid, disabled = false }) {
  const [translate, setTranslate] = useState(0); // negative = revealing the button
  const startX = useRef(0);
  const startY = useRef(0);
  const dragging = useRef(false);
  const verticalLock = useRef(false);
  const containerRef = useRef(null);

  const REVEAL = 88; // px width of revealed button
  const SNAP_THRESHOLD = 50;

  useEffect(() => {
    if (disabled) return;
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
      dragging.current = true;
      verticalLock.current = false;
    };

    const onTouchMove = (e) => {
      if (!dragging.current) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;
      // Lock to vertical scroll if the user is scrolling the page
      if (!verticalLock.current && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
        verticalLock.current = true;
        return;
      }
      if (verticalLock.current) return;
      // Combined offset — start from current translate
      const next = Math.min(0, Math.max(-REVEAL, translate + dx));
      // We only update on significant movement to avoid jitter
      if (Math.abs(dx) > 4) {
        setTranslate(next);
      }
    };

    const onTouchEnd = (e) => {
      if (!dragging.current) return;
      dragging.current = false;
      if (verticalLock.current) {
        verticalLock.current = false;
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const dx = endX - startX.current;
      // Snap open if pulled past threshold OR already mostly open
      const newPos = translate + dx;
      if (newPos < -SNAP_THRESHOLD) {
        setTranslate(-REVEAL);
      } else {
        setTranslate(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [translate, disabled]);

  // Close when user taps anywhere outside this row
  useEffect(() => {
    if (translate === 0) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setTranslate(0);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [translate]);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setTranslate(0);
    onDelete?.();
  };

  return (
    <div ref={containerRef} className="relative overflow-hidden rounded-2xl" data-testid={testid}>
      {/* Delete button revealed underneath */}
      <button
        type="button"
        onClick={handleDeleteClick}
        aria-label={label}
        className="absolute right-0 top-0 bottom-0 w-[88px] bg-[#d92027] text-white flex flex-col items-center justify-center gap-1 text-xs font-semibold"
        style={{ opacity: translate < -10 ? 1 : 0, transition: 'opacity 0.15s ease' }}
        data-testid={testid ? `${testid}-delete-action` : undefined}
      >
        <Trash2 size={20} />
        {label}
      </button>

      {/* The card content slides over the button */}
      <div
        style={{
          transform: `translateX(${translate}px)`,
          transition: dragging.current ? 'none' : 'transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
